import {
  Alert,
  Button,
  Group,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core'
import { IconFocusCentered, IconMinus, IconPlus, IconRestore } from '@tabler/icons-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Cropper } from 'react-advanced-cropper'
import { useTranslation } from 'react-i18next'
import 'react-advanced-cropper/dist/style.css'
import {
  cropCoordinates,
  normalizeCrop,
  previewDensity,
  retinaShortfall,
} from '../../utils/imageCrops'
import classes from '../../styles/StudioEditorV2.module.css'

const FOCUS_POSITIONS = [
  ['editorV2.form.crop.topLeft', 0, 0],
  ['editorV2.form.crop.top', 0.5, 0],
  ['editorV2.form.crop.topRight', 1, 0],
  ['editorV2.form.crop.left', 0, 0.5],
  ['editorV2.form.crop.center', 0.5, 0.5],
  ['editorV2.form.crop.right', 1, 0.5],
  ['editorV2.form.crop.bottomLeft', 0, 1],
  ['editorV2.form.crop.bottom', 0.5, 1],
  ['editorV2.form.crop.bottomRight', 1, 1],
]
const CENTER = 'editorV2.form.crop.center'

export function ImageCropDialog({
  opened,
  onClose,
  onApply,
  sourceUrl,
  crop,
  target,
  label,
}) {
  const { t } = useTranslation()
  const cropperRef = useRef(null)
  const [error, setError] = useState(null)
  const [warning, setWarning] = useState(null)
  const aspectRatio = useMemo(
    () => Number(target?.width || 1) / Number(target?.height || 1),
    [target],
  )
  const cropSource = sourceUrl && /^https?:\/\//i.test(sourceUrl)
    && new URL(sourceUrl).origin !== window.location.origin
    ? `/api/studio-editor-v2/media-library/fetch?url=${encodeURIComponent(sourceUrl)}`
    : sourceUrl

  useEffect(() => {
    if (opened) {
      setError(null)
      setWarning(null)
    }
  }, [opened, sourceUrl, target?.height, target?.width])

  const defaultSize = ({ imageSize }) => {
    const restored = cropCoordinates(crop, imageSize)
    if (restored) return { width: restored.width, height: restored.height }
    if (imageSize.width / imageSize.height > aspectRatio) {
      return { width: imageSize.height * aspectRatio, height: imageSize.height }
    }
    return { width: imageSize.width, height: imageSize.width / aspectRatio }
  }

  const defaultPosition = ({ imageSize, coordinates }) => {
    const restored = cropCoordinates(crop, imageSize)
    if (restored) return { left: restored.left, top: restored.top }
    return {
      left: (imageSize.width - coordinates.width) / 2,
      top: (imageSize.height - coordinates.height) / 2,
    }
  }

  const setFocus = (horizontal, vertical) => {
    const cropper = cropperRef.current
    if (!cropper) return
    cropper.setCoordinates(({ coordinates, imageSize }) => ({
      left: (imageSize.width - coordinates.width) * horizontal,
      top: (imageSize.height - coordinates.height) * vertical,
    }))
  }

  const updateResolutionWarning = () => {
    const cropper = cropperRef.current
    if (!cropper) return
    const normalized = normalizeCrop(cropper.getCoordinates(), cropper.getImage())
    const shortfall = retinaShortfall(normalized, cropper.getImage(), target)
    setWarning(shortfall
      ? (
        t('editorV2.form.crop.warningUsable', {
          cropWidth: shortfall.cropWidth,
          cropHeight: shortfall.cropHeight,
          requiredWidth: shortfall.requiredWidth,
          requiredHeight: shortfall.requiredHeight,
        })
      )
      : null)
  }

  const apply = () => {
    const cropper = cropperRef.current
    if (!cropper) return
    const coordinates = cropper.getCoordinates()
    const image = cropper.getImage()
    const normalized = normalizeCrop(coordinates, image)
    const shortfall = retinaShortfall(normalized, image, target)
    setWarning(shortfall
      ? (
        t('editorV2.form.crop.warningUpscaled', {
          cropWidth: shortfall.cropWidth,
          cropHeight: shortfall.cropHeight,
          requiredWidth: shortfall.requiredWidth,
          requiredHeight: shortfall.requiredHeight,
        })
      )
      : null)

    const density = previewDensity()
    const canvas = cropper.getCanvas({
      width: Math.round(Number(target.width) * density),
      height: Math.round(Number(target.height) * density),
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
      // The largest format (billboard 970×250 area budget) at 2×.
      maxArea: 970 * 600 * 4,
    })
    if (!canvas) {
      setError(t('editorV2.form.crop.previewFailed'))
      return
    }

    let dataUrl = canvas.toDataURL('image/webp', 0.82)
    if (!dataUrl.startsWith('data:image/webp')) {
      dataUrl = canvas.toDataURL('image/jpeg', 0.86)
    }
    setError(null)
    onApply({ crop: normalized, previewUrl: dataUrl })
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('editorV2.form.crop.title', { label, labelLower: label?.toLowerCase() })}
      size="xl"
      centered
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {t('editorV2.form.crop.intro', { width: target?.width, height: target?.height })}
        </Text>
        {sourceUrl ? (
          <div className={classes.cropperFrame}>
            <Cropper
              key={`${sourceUrl}-${target?.width}x${target?.height}`}
              ref={cropperRef}
              src={cropSource}
              crossOrigin="anonymous"
              checkOrientation
              imageRestriction="stencil"
              defaultSize={defaultSize}
              defaultPosition={defaultPosition}
              priority="coordinates"
              onChange={() => {
                if (error) setError(null)
                updateResolutionWarning()
              }}
              stencilProps={{
                aspectRatio,
                grid: true,
                movable: true,
                resizable: true,
              }}
              backgroundClassName={classes.cropperBackground}
              className={classes.cropper}
            />
          </div>
        ) : (
          <Paper withBorder p="xl"><Text c="dimmed">{t('editorV2.form.crop.reading')}</Text></Paper>
        )}
        <SimpleGrid cols={3} spacing={5}>
          {FOCUS_POSITIONS.map(([name, horizontal, vertical]) => (
            <Button
              key={name}
              size="compact-xs"
              variant={name === CENTER ? 'light' : 'default'}
              leftSection={name === CENTER ? <IconFocusCentered size={13} /> : null}
              onClick={() => setFocus(horizontal, vertical)}
              disabled={!sourceUrl}
            >
              {t(name)}
            </Button>
          ))}
        </SimpleGrid>
        <Group justify="space-between">
          <Group gap={5}>
            <Button
              size="xs"
              variant="default"
              leftSection={<IconMinus size={15} />}
              onClick={() => cropperRef.current?.zoomImage(0.9)}
            >
              {t('editorV2.form.crop.zoomOut')}
            </Button>
            <Button
              size="xs"
              variant="default"
              leftSection={<IconPlus size={15} />}
              onClick={() => cropperRef.current?.zoomImage(1.1)}
            >
              {t('editorV2.form.crop.zoomIn')}
            </Button>
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconRestore size={15} />}
              onClick={() => cropperRef.current?.reset()}
            >
              {t('editorV2.form.crop.reset')}
            </Button>
          </Group>
          <Text size="xs" c="dimmed">
            {t('editorV2.form.crop.retinaSize', { width: Number(target?.width || 0) * 2, height: Number(target?.height || 0) * 2 })}
          </Text>
        </Group>
        {warning && <Alert color="yellow">{warning}</Alert>}
        {error && <Alert color="red">{error}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('editorV2.form.crop.cancel')}</Button>
          <Button color="pink" onClick={apply} disabled={!sourceUrl}>{t('editorV2.form.crop.apply')}</Button>
        </Group>
      </Stack>
    </Modal>
  )
}
